(() => {
  'use strict';
  if (window.AWReplicaAttributionBridge?.version) return;

  const KEYS = {
    active: 'aw_garage_active_replica_v1',
    replicas: 'aw_garage_replicas_v1',
    configurator: 'aw_airsoft_configurator',
    context: 'aw_airsoft_context_v1',
    buildLinks: 'aw_build_replica_links_v1'
  };

  const normalize = (value = '') => String(value)
    .trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const readJson = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
    catch (_) { return fallback; }
  };

  const getActiveReplica = () => {
    const activeId = String(readJson(KEYS.active, '') || '');
    const replicas = readJson(KEYS.replicas, []);
    if (!activeId || !Array.isArray(replicas)) return null;
    return replicas.find((replica) => String(replica?.id || '') === activeId) || null;
  };

  const formProperty = (body, name) => {
    if (body instanceof FormData || body instanceof URLSearchParams) return String(body.get(`properties[${name}]`) || '');
    return '';
  };

  const jsonPayload = (body) => {
    if (typeof body !== 'string') return null;
    try { const parsed = JSON.parse(body); return parsed && typeof parsed === 'object' ? parsed : null; }
    catch (_) { return null; }
  };

  const firstProperties = (body) => {
    if (body instanceof FormData || body instanceof URLSearchParams) return {
      platform: formProperty(body, 'Plateforme AW') || formProperty(body, '_AW Platform'),
      manufacturer: formProperty(body, 'Fabricant AW') || formProperty(body, '_AW Manufacturer'),
      type: formProperty(body, 'Type de réplique AW') || formProperty(body, '_AW Replica Type'),
      model: formProperty(body, 'Réplique exacte AW') || formProperty(body, '_AW Model'),
      replicaId: formProperty(body, '_AW Replica ID')
    };
    const parsed = jsonPayload(body);
    if (!parsed) return {};
    const properties = Array.isArray(parsed.items) ? (parsed.items[0]?.properties || {}) : (parsed.properties || {});
    return {
      platform: properties['Plateforme AW'] || properties['_AW Platform'] || '',
      manufacturer: properties['Fabricant AW'] || properties['_AW Manufacturer'] || '',
      type: properties['Type de réplique AW'] || properties['_AW Replica Type'] || '',
      model: properties['Réplique exacte AW'] || properties['_AW Model'] || '',
      replicaId: properties['_AW Replica ID'] || ''
    };
  };

  const configuratorContext = () => {
    const root = document.querySelector('[data-aw-configurator]');
    if (!root) return null;
    const state = readJson(root.dataset.storageKey || KEYS.configurator, {});
    const shared = readJson(KEYS.context, {});
    if (!state?.platform) return null;
    const activeId = String(readJson(KEYS.active, '') || '');
    const links = readJson(KEYS.buildLinks, {});
    const savedId = String(state.savedId || '');
    const linkedBuild = savedId && links && String(links[savedId] || '') === activeId ? savedId : '';
    return {
      platform: state.platform || '',
      manufacturer: state.manufacturer || '',
      type: state.type || '',
      model: state.modelLabel || state.model || shared.modelLabel || shared.model || '',
      buildId: linkedBuild
    };
  };

  const productCompatibilityContext = () => {
    const root = document.querySelector('[data-aw-compatibility]');
    if (!root) return null;
    const platform = root.querySelector('[data-aw-platform-select]')?.value || '';
    if (!platform) return null;
    return {
      platform,
      manufacturer: root.querySelector('[data-aw-manufacturer-select]')?.value || '',
      type: root.querySelector('[data-aw-replica-type-select]')?.value || '',
      model: '',
      buildId: ''
    };
  };

  const stripManufacturer = (model = '', manufacturer = '') => {
    let value = normalize(model);
    const maker = normalize(manufacturer);
    if (maker && value.startsWith(`${maker}-`)) value = value.slice(maker.length + 1);
    return value;
  };

  const sameModel = (left = '', right = '', manufacturer = '') => {
    if (!left || !right) return true;
    return stripManufacturer(left, manufacturer) === stripManufacturer(right, manufacturer);
  };

  const sameBaseContext = (stored, direct) => {
    if (!stored || !direct) return false;
    if (normalize(stored.platform || '') !== normalize(direct.platform || '')) return false;
    const directManufacturer = normalize(direct.manufacturer || '');
    const storedManufacturer = normalize(stored.manufacturer || '');
    if (directManufacturer && storedManufacturer && directManufacturer !== storedManufacturer) return false;
    const directType = normalize(direct.type || '');
    const storedType = normalize(stored.type || '');
    if (directType && storedType && directType !== storedType) return false;
    return true;
  };

  const sameContext = (stored, direct) => {
    if (!sameBaseContext(stored, direct)) return false;
    return sameModel(stored.model || '', direct.model || '', direct.manufacturer || stored.manufacturer || '');
  };

  const contextFor = (body) => {
    const direct = firstProperties(body);
    if (direct.replicaId) return { existingReplicaId: direct.replicaId };
    if (direct.platform) {
      const stored = configuratorContext();
      const baseMatches = sameBaseContext(stored, direct);
      const merged = {
        ...direct,
        model: direct.model || (baseMatches ? (stored?.model || '') : '')
      };
      return { ...merged, buildId: sameContext(stored, merged) ? (stored?.buildId || '') : '' };
    }
    return configuratorContext() || productCompatibilityContext();
  };

  const matchingReplica = (context) => {
    if (!context || context.existingReplicaId) return null;
    const replica = getActiveReplica();
    if (!replica?.id) return null;
    const contextPlatform = normalize(context.platform || '');
    const replicaPlatform = normalize(replica.platform || '');
    if (!contextPlatform || !replicaPlatform || contextPlatform !== replicaPlatform) return null;

    const contextManufacturer = normalize(context.manufacturer || '');
    const replicaManufacturer = normalize(replica.manufacturer || '');
    if (contextManufacturer && replicaManufacturer && contextManufacturer !== replicaManufacturer) return null;

    const contextType = normalize(context.type || '');
    const replicaType = normalize(replica.type || '');
    if (contextType && replicaType && contextType !== replicaType) return null;

    if (context.model && replica.model && !sameModel(context.model, replica.model, context.manufacturer || replica.manufacturer || '')) return null;
    return replica;
  };

  const attributionProperties = (replica, context = {}) => {
    const properties = {
      '_AW Replica ID': String(replica.id || '').slice(0, 120),
      '_AW Replica Label': String(replica.nickname || replica.model || 'Réplique AW').slice(0, 120)
    };
    if (context.buildId) properties['_AW Build ID'] = String(context.buildId).slice(0, 120);
    return properties;
  };

  const decorateBody = (body, properties) => {
    if (!body || !properties || !Object.keys(properties).length) return body;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      Object.entries(properties).forEach(([key, value]) => { if (!body.get(`properties[${key}]`)) body.set(`properties[${key}]`, value); });
      return body;
    }
    if (typeof body === 'string') {
      const parsed = jsonPayload(body);
      if (!parsed) return body;
      if (Array.isArray(parsed.items)) parsed.items = parsed.items.map((item) => ({ ...item, properties: { ...properties, ...(item.properties || {}) } }));
      else parsed.properties = { ...properties, ...(parsed.properties || {}) };
      return JSON.stringify(parsed);
    }
    return body;
  };

  const install = () => {
    if (!window.fetch || window.fetch.__awReplicaAttributionBridge === true) return false;
    const innerFetch = window.fetch.bind(window);
    const wrapped = (input, init = {}) => {
      let nextInit = init;
      try {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const method = String(init?.method || input?.method || 'GET').toUpperCase();
        const cartAdd = method === 'POST' && /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(url);
        if (cartAdd && Object.prototype.hasOwnProperty.call(init, 'body')) {
          const context = contextFor(init.body);
          const replica = matchingReplica(context);
          if (replica) nextInit = { ...init, body: decorateBody(init.body, attributionProperties(replica, context)) };
        }
      } catch (_) {}
      return innerFetch(input, nextInit);
    };
    wrapped.__awReplicaAttributionBridge = true;
    wrapped.__awReplicaAttributionInner = innerFetch;
    window.fetch = wrapped;
    return true;
  };

  window.AWReplicaAttributionBridge = {
    version: '1.1.1',
    getActiveReplica,
    contextFor,
    matchingReplica,
    decorateBody,
    sameBaseContext,
    sameModel,
    install,
    contextPriority: 'visible-cart-properties-then-matching-configurator-model',
    modelPolicy: 'exact-model-must-match-when-both-known',
    buildPolicy: 'same-context-only'
  };

  install();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install, { once: true });
  document.addEventListener('shopify:section:load', install);
})();